import {
  ButtonHTMLAttributes,
  ComponentType,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { ISelectOption } from '@northladder/utilities';

/**
 * Supported screen size categories.
 */
export type IScreenSize = 'mobile' | 'tablet' | 'desktop';

/**
 * Animation types and configurations
 */
export interface IAnimationConfig {
  /** Badge animation type */
  badgeAnimation?: 'bounce' | 'pulse' | 'wiggle' | 'fade' | 'slide' | 'none';
  /** Animation delay in seconds */
  delay?: number;
  /** Animation duration in seconds */
  duration?: number;
  /** Option hover animation type */
  optionHoverAnimation?: 'highlight' | 'scale' | 'glow' | 'none';
  /** Popover animation type */
  popoverAnimation?: 'scale' | 'slide' | 'fade' | 'flip' | 'none';
}

/**
 * Option interface for MultiSelect component
 */
export interface IMultiSelectOption extends ISelectOption {
  /** Optional icon component to display alongside the option. */
  icon?: ComponentType<{ className?: string }>;
  /** Custom styling for the option */
  style?: {
    /** Custom badge color */
    badgeColor?: string;
    /** Gradient background for badge */
    gradient?: string;
    /** Custom icon color */
    iconColor?: string;
  };
}

/**
 * Group interface for organizing options
 */
export interface IMultiSelectGroup {
  /** Group heading */
  heading: string;
  /** Options in this group */
  options: IMultiSelectOption[];
}

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
export const multiSelectVariants = cva(
  'm-1 transition-all duration-300 ease-in-out',
  {
    variants: {
      variant: {
        default:
          'border-foreground/10 text-foreground bg-card hover:bg-card/80',
        secondary:
          'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
      badgeAnimation: {
        bounce: 'hover:-translate-y-1 hover:scale-110',
        pulse: 'hover:animate-pulse',
        wiggle: 'hover:animate-wiggle',
        fade: 'hover:opacity-80',
        slide: 'hover:translate-x-1',
        none: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      badgeAnimation: 'bounce',
    },
  }
);

interface IDeviceConfiguration {
  maxCount?: number;
  hideIcons?: boolean;
  compactMode?: boolean;
}

/**
 * Imperative methods exposed through ref
 */
export interface IMultiSelectRef {
  /**
   * Clear all selected values
   */
  clear: () => void;
  /**
   * Focus the component
   */
  focus: () => void;
  /**
   * Get current selected values
   */
  getSelectedValues: () => string[];
  /**
   * Programmatically reset the component to its default value
   */
  reset: () => void;
  /**
   * Set selected values programmatically
   */
  setSelectedValues: (values: string[]) => void;
}

/**
 * Props for MultiSelect component
 */
export interface IMultiSelectProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'animationConfig'>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Advanced animation configuration for different component parts.
   * Optional, allows fine-tuning of various animation effects.
   */
  animationConfig?: IAnimationConfig;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * If true, allows the component to grow and shrink with its content.
   * If false, uses fixed width behavior.
   * Optional, defaults to false.
   */
  autoSize?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * If true, automatically closes the popover after selecting an option.
   * Useful for single-selection-like behavior or mobile UX.
   * Optional, defaults to false.
   */
  closeOnSelect?: boolean;

  /**
   * If true, automatically removes duplicate options based on their value.
   * Optional, defaults to false (shows warning in dev mode instead).
   */
  deduplicateOptions?: boolean;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * If true, disables the component completely.
   * Optional, defaults to false.
   */
  disabled?: boolean;

  /**
   * Custom empty state message when no options match search.
   * Optional, defaults to "No results found."
   */
  emptyIndicator?: ReactNode;

  /**
   * If true, disables the select all functionality.
   * Optional, defaults to false.
   */
  hideSelectAll?: boolean;
  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * Maximum width for the component.
   * Optional, defaults to 100% of container.
   * Component will not exceed container boundaries.
   */
  maxWidth?: string;

  /**
   * Minimum width for the component.
   * Optional, defaults to auto-sizing based on content.
   * When set, component will not shrink below this width.
   */
  minWidth?: string;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /**
   * An array of option objects or groups to be displayed in the multi-select component.
   */
  options: IMultiSelectOption[] | IMultiSelectGroup[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Custom CSS class for the placeholder text when no options are selected.
   * Optional, can be used to customize placeholder text color and styling.
   */
  placeholderClassName?: string;

  /**
   * Custom CSS class for the popover content.
   * Optional, can be used to customize popover appearance.
   */
  popoverClassName?: string;

  /**
   * If true, the component will reset its internal state when defaultValue changes.
   * Useful for React Hook Form integration and form reset functionality.
   * Optional, defaults to true.
   */
  resetOnDefaultValueChange?: boolean;

  /**
   * Responsive configuration for different screen sizes.
   * Allows customizing maxCount and other properties based on viewport.
   * Can be boolean true for default responsive behavior or an object for custom configuration.
   */
  responsive?:
    | boolean
    | {
        /** Configuration for mobile devices (< 640px) */
        mobile?: IDeviceConfiguration;
        /** Configuration for tablet devices (640px - 1024px) */
        tablet?: IDeviceConfiguration;
        /** Configuration for desktop devices (> 1024px) */
        desktop?: IDeviceConfiguration;
      };

  /**
   * If true, shows search functionality in the popover.
   * If false, hides the search input completely.
   * Optional, defaults to true.
   */
  searchable?: boolean;

  /**
   * If true, shows badges in a single line with horizontal scroll.
   * If false, badges wrap to multiple lines.
   * Optional, defaults to false.
   */
  singleLine?: boolean;
}

interface IWidthConstraints {
  maxWidth: string;
  minWidth: string;
  width: string;
}

interface IResponsiveSettings {
  compactMode: boolean;
  hideIcons: boolean;
  maxCount: number;
}

export interface ITriggerBtnProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  animDelay: number;
  animDuration: number;
  autoSize: boolean;
  buttonRef: Ref<HTMLButtonElement> | null;
  classBadgeAnimation: string;
  isDisabled: boolean;
  isMobileScreen: boolean;
  isPopoverOpen: boolean;
  isSingleLine: boolean;
  listboxId: string;
  onClearExtraOptions: MouseEventHandler<SVGSVGElement>;
  onClearFilters: () => void;
  onGetOptionByValue: (value: string) => IMultiSelectOption | undefined;
  onToggleOption: (optionValue: string) => void;
  onTogglePopover: () => void;
  placeholder: string;
  placeholderClassName?: string;
  responsiveSettings: IResponsiveSettings;
  selectedCountId: string;
  selectedItemsCount: number;
  selectedValues: string[];
  total: number;
  triggerDescriptionId: string;
  variant: IMultiSelectProps['variant'];
  widthConstraints: IWidthConstraints;
}
